import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Eye, Trash2, Edit } from "lucide-react";
import { usePostStore } from "@/stores/postStore";
import { useDebounce } from "@/hooks/useDebounce";
import ModalViewPost from "@/components/movie/ModalViewPost";
import ModalUpdatePost from "@/components/movie/ModalUpdatePost";
import ModalDeletePost from "@/components/movie/ModalDeletePost";
import ModalAddPost from "@/components/movie/ModalAddPost";

// Note: formatDate removed as not currently used in compact table UI

export default function FilmListPage() {
  const { isLoading, meta, posts, fetchPosts, deletePost, setPage } =
    usePostStore();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const deferredPosts = useDeferredValue(posts);
  const [viewing, setViewing] = useState<any | null>(null);
  const [openView, setOpenView] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [deleting, setDeleting] = useState<any | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);

  // Build params
  const queryParams = useMemo(() => {
    return {
      pageIndex: meta.page,
      pageSize: meta.limit,
      search: debouncedSearch || undefined,
    } as const;
  }, [meta.page, meta.limit, debouncedSearch]);

  // Fetch posts on mount and when filters/page change
  useEffect(() => {
    fetchPosts({ ...queryParams });
  }, [queryParams, fetchPosts]);

  const handleSearch = useCallback(() => {
    setPage(1);
  }, [setPage]);

  // Reset về trang 1 khi chuỗi tìm kiếm thay đổi (sau debounce)
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, setPage]);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const postsList = useMemo(() => {
    if (isLoading) {
      return (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-12 w-full bg-gray-100 animate-pulse rounded-md"
            />
          ))}
        </div>
      );
    }
    if (!deferredPosts || deferredPosts.length === 0) {
      return (
        <div className="text-center py-10 border rounded-lg bg-gray-50">
          <div className="text-gray-400 text-lg mb-1">Không có post nào</div>
          <div className="text-gray-500">Thử thay đổi từ khóa tìm kiếm</div>
        </div>
      );
    }
    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60 dark:supports-[backdrop-filter]:bg-gray-800/60 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Poster
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Tên phim
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Mô tả
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Hành động
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {deferredPosts.map((item: any) => {
                const desc: string = item?.description || "";
                const cleanDesc = desc.replace(/<[^>]*>/g, "");
                const words = cleanDesc.trim().split(/\s+/);
                const shortDesc =
                  words.length > 18
                    ? words.slice(0, 18).join(" ") + " ..."
                    : cleanDesc;
                return (
                  <tr
                    key={item?._id || item?.id || Math.random()}
                    className="transition-all hover:bg-gray-50/60 dark:hover:bg-gray-800/60"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item?.thumbnail ? (
                        <img
                          src={
                            item.thumbnail.startsWith("http")
                              ? item.thumbnail
                              : `https://api.ophim69.com/api/v1/${item.thumbnail}`
                          }
                          alt={item?.name || "Poster"}
                          className="w-14 h-20 object-cover rounded border border-gray-200 bg-gray-50"
                        />
                      ) : (
                        <div className="w-14 h-20 bg-gray-100 rounded" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white truncate max-w-[240px]">
                      {item?.name || "(Trống)"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      <div className="relative group max-w-[360px] truncate cursor-default">
                        {shortDesc || "(Trống)"}
                        {cleanDesc && cleanDesc.length > shortDesc.length && (
                          <div className="absolute left-0 top-full mt-1 hidden group-hover:block w-[420px] bg-white border shadow-lg rounded p-3 z-20">
                            <div
                              className="text-sm text-gray-700"
                              dangerouslySetInnerHTML={{ __html: desc }}
                            />
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="cursor-pointer"
                          onClick={() => {
                            setViewing(item);
                            setOpenView(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="cursor-pointer"
                          onClick={() => {
                            setEditing(item);
                            setOpenEdit(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDeleting(item);
                            setOpenDelete(true);
                          }}
                          className="cursor-pointer text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {meta.totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 mt-6 flex-wrap">
            <Button
              variant="outline"
              onClick={() => handlePageChange(meta.page - 1)}
              disabled={meta.page === 1}
              className="cursor-pointer"
            >
              Trước
            </Button>

            {(() => {
              const total = meta.totalPages;
              const current = meta.page;
              const pages: Array<number | string> = [];
              const pushRange = (start: number, end: number) => {
                for (let i = start; i <= end; i++) pages.push(i);
              };

              if (total <= 7) {
                pushRange(1, total);
              } else {
                pages.push(1);
                const left = Math.max(2, current - 1);
                const right = Math.min(total - 1, current + 1);

                if (left > 2) pages.push("...");
                pushRange(left, right);
                if (right < total - 1) pages.push("...");
                pages.push(total);
              }

              return pages.map((p, idx) =>
                typeof p === "number" ? (
                  <Button
                    key={`p-${p}`}
                    variant={p === current ? "default" : "outline"}
                    onClick={() => handlePageChange(p)}
                    className="cursor-pointer"
                  >
                    {p}
                  </Button>
                ) : (
                  <span
                    key={`dots-${idx}`}
                    className="px-2 text-gray-500 select-none"
                  >
                    …
                  </span>
                )
              );
            })()}

            <Button
              variant="outline"
              onClick={() => handlePageChange(meta.page + 1)}
              disabled={meta.page === meta.totalPages}
              className="cursor-pointer"
            >
              Sau
            </Button>
          </div>
        )}
      </>
    );
  }, [deferredPosts, isLoading, meta, handlePageChange, deletePost]);

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý phim (Post)
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500 dark:text-gray-400">
                  Danh sách post phim từ hệ thống
                </p>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  {posts.length} phim
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Tìm kiếm</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Từ khóa
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Tìm theo tên phim, mô tả..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                    type="text"
                  />
                  <Button onClick={handleSearch} className="cursor-pointer">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Posts List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Danh sách phim
                <Badge variant="secondary" className="ml-2">
                  {meta.totalItems} mục
                </Badge>
              </CardTitle>
              <Button
                className="cursor-pointer"
                onClick={() => setOpenAdd(true)}
              >
                Thêm bài viết
              </Button>
            </div>
          </CardHeader>
          <CardContent>{postsList}</CardContent>
        </Card>
      </div>

      {/* Modal view post */}
      <ModalViewPost
        isOpen={openView}
        onClose={() => setOpenView(false)}
        post={viewing}
      />

      {/* Modal update post */}
      <ModalUpdatePost
        isOpen={openEdit}
        onClose={() => setOpenEdit(false)}
        post={editing}
        onCompleted={() => {
          // refresh current page
          fetchPosts({
            pageIndex: meta.page,
            pageSize: meta.limit,
            search: debouncedSearch || undefined,
          });
        }}
      />

      {/* Modal delete post */}
      <ModalDeletePost
        isOpen={openDelete}
        onClose={() => setOpenDelete(false)}
        post={deleting}
        onCompleted={() => {
          fetchPosts({
            pageIndex: meta.page,
            pageSize: meta.limit,
            search: debouncedSearch || undefined,
          });
        }}
      />

      {/* Modal add post */}
      <ModalAddPost
        isOpen={openAdd}
        onClose={() => setOpenAdd(false)}
        onCompleted={() => {
          fetchPosts({
            pageIndex: meta.page,
            pageSize: meta.limit,
            search: debouncedSearch || undefined,
          });
        }}
      />
    </DashboardLayout>
  );
}
